import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_VisibilityState,
  MRT_GlobalFilterTextField,
  MRT_ToggleFullScreenButton,
} from 'material-react-table';
import { Box, Stack, Menu, MenuItem, Chip, Alert, Tooltip } from '@mui/material';
import { Edit, PersonAdd, Search } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { userService } from '../components/user-management/user.api';
import { useMRTStyling } from '../hooks/useMRTStyling';
import { CustomManageColumnsMenu } from '../components/common/CustomManageColumnsMenu';
import { IconOverTextButton } from '../components/common/IconOverTextButton';
import { UserDTO } from '../components/user-management/auth.type';
import { useNavigate } from 'react-router-dom';
import { Typography } from 'components/index';
import { Button } from 'components/index';
import WhiteFab from '../components/common/WhiteFab';
import CreateUserDialog from '../components/user-management/CreateUserDialog';
import { getFullName } from '../common/utils/common.util';

interface UserWithFullName extends UserDTO {
  fullName: string;
}

const UsersList: React.FC = () => {
  const navigate = useNavigate();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [users, setUsers] = useState<UserWithFullName[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserWithFullName | null>(null);
  const [columnsMenuAnchorEl, setColumnsMenuAnchorEl] = useState<null | HTMLElement>(null);

  const [isCreateUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');

  // Available roles in the system
  const availableRoles = ['System Admin', 'CDCC Admin', 'Inspector'];

  const { commonMRTStyling, getOnSortingChangeWithToast } = useMRTStyling();
  const [sorting, setSorting] = useState<any>([]);

  // Fetch users from API
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getAllUsers();
      const usersWithFullName = response.map((user: UserDTO) => ({
        ...user,
        fullName: getFullName(user),
      }));
      setUsers(usersWithFullName);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.message || 'Failed to fetch users');
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle role toggle (add/remove role)
  const handleRoleToggle = async (user: UserWithFullName, role: string) => {
    try {
      const currentRoles = user.roles || [];
      const hasRole = currentRoles.includes(role);

      let newRoles: string[];
      if (hasRole) {
        // Remove role
        newRoles = currentRoles.filter(r => r !== role);
      } else {
        // Add role
        newRoles = [...currentRoles, role];
      }

      await userService.assignRoles({
        UserIds: [user.id],
        Roles: newRoles,
      });

      // Update local state
      setUsers(prev => prev.map(u => (u.id === user.id ? { ...u, roles: newRoles } : u)));

      const action = hasRole ? 'removed' : 'added';
      toast.success(`Role ${role} ${action} successfully for ${user.fullName}`);
    } catch (err: any) {
      console.error('Error updating role:', err);
      toast.error('Failed to update role');
    }
  };

  // Handle Create User Dialog
  const handleOpenCreateUserDialog = () => {
    setCreateUserDialogOpen(true);
  };

  const handleCloseCreateUserDialog = () => {
    setCreateUserDialogOpen(false);
    // Refresh the users list after creating a new user
    fetchUsers();
  };

  // Handle menu actions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: UserWithFullName) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  // Define columns
  const columns = useMemo<MRT_ColumnDef<UserWithFullName>[]>(
    () => [
      {
        accessorKey: 'fullName',
        header: 'Name',
        size: 200,
        enableHiding: false,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 250,
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        accessorKey: 'roles',
        header: 'Roles',
        size: 200,
        enableSorting: false,
        enableColumnFilter: true,
        Cell: ({ row }) => {
          const roles = row.original.roles || [];
          return (
            <Stack direction='row' spacing={1} flexWrap='wrap'>
              {roles.map((role, index) => (
                <Chip
                  key={index}
                  label={role}
                  size='small'
                  color={role === 'System Admin' ? 'error' : role === 'CDCC Admin' ? 'warning' : 'primary'}
                  variant='outlined'
                />
              ))}
            </Stack>
          );
        },
      },
    ],
    [],
  );

  // Define table
  const table = useMaterialReactTable({
    columns,
    data: users,
    getRowId: row => row.id,
    ...commonMRTStyling,
    state: {
      isLoading: loading,
      columnVisibility,
      globalFilter,
      isFullScreen,
      sorting,
    },
    onSortingChange: getOnSortingChangeWithToast(sorting as any, setSorting as any),
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }: { row: MRT_Row<UserWithFullName> }) => (
      <Stack direction='row' spacing={1} alignItems='center' justifyContent='center'>
        <Tooltip title='Toggle Roles' arrow={false}>
          <span>
            <WhiteFab
              size='small'
              sx={{ width: 32, height: 32, minHeight: 32 }}
              icon={<Edit fontSize='small' />}
              textColor='primary'
              onClick={e => handleMenuOpen(e, row.original)}
            />
          </span>
        </Tooltip>
      </Stack>
    ),
    initialState: {
      showGlobalFilter: true,
      columnPinning: {
        left: ['fullName'],
        right: ['mrt-row-actions'],
      },
    },
    enableGlobalFilter: true,
    enableSorting: true,
    enableColumnResizing: true,
    enableFullScreenToggle: true,
    layoutMode: 'grid',
    enableTopToolbar: isFullScreen,
    onIsFullScreenChange: setIsFullScreen,
    muiTopToolbarProps: {
      sx: {
        backgroundColor: 'white !important',
        justifyContent: 'flex-end !important',
        display: 'flex',
        '& .MuiToolbar-root.MuiToolbar-gutters': {
          display: 'none',
        },
      },
    },
    // Only render fullscreen toggle in internal toolbar actions so column toggle won't show
    renderToolbarInternalActions: ({ table }) => (
      <Stack direction='row' spacing={1} alignItems='center'>
        <MRT_ToggleFullScreenButton table={table} />
      </Stack>
    ),
    enablePagination: false,
    enableBottomToolbar: false,
    muiTableBodyRowProps: ({ row }: { row: MRT_Row<UserWithFullName> }) => {
      // Use zebra striping for alternating rows
      const backgroundColor = row.index % 2 === 0 ? '#f9f9f9' : '#ffffff';

      return {
        sx: {
          cursor: 'pointer',
          backgroundColor: backgroundColor,
          height: 36,
          '& .MuiTableCell-root': {
            py: 0.25,
          },
          '&:hover': {
            backgroundColor: (theme: any) => theme.palette.action.hover,
          },
          // Style pinned columns to match row background
          '& td[data-pinned="true"]::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: backgroundColor,
            zIndex: -1,
          },
          '& td[data-pinned="true"]': {
            zIndex: 5,
          },
        },
      };
    },
    muiTableBodyCellProps: ({ row }: { row: MRT_Row<UserWithFullName> }) => ({
      sx: {
        border: 'none',
        py: 0.25,
        backgroundColor: row.index % 2 === 0 ? '#f9f9f9' : '#ffffff',
      },
    }),
    muiTableContainerProps: {
      sx: {
        flex: 1,
        maxHeight: 'calc(100vh - 200px)',
        overflow: 'auto',
        backgroundColor: 'white',
        '& .MuiTable-root': {
          backgroundColor: 'white',
        },
        '& .MuiTableHead-root': {
          backgroundColor: 'white',
        },
        '& .MuiTableBody-root': {
          backgroundColor: 'white',
        },
        '& .MuiTableCell-root': {
          backgroundColor: 'white',
          border: 'none',
        },
        // Keep header and footer in view while scrolling
        '& thead, & tfoot': {
          position: 'sticky',
          backgroundColor: 'white',
          zIndex: 2,
        },
        '& thead': {
          top: 0,
          zIndex: 3,
        },
      },
    },
    muiTableHeadProps: {
      sx: {
        padding: 0,
        color: 'white',
      },
    },
    muiTableHeadCellProps: {
      sx: theme => ({
        backgroundColor: theme.palette.primary.main + ' !important',
        color: '#ffffff',
        justifyContent: 'center',
        padding: '4px 8px',
        border: 'none',
        '&[data-pinned="true"]:not([role="columnheader"])::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme => theme.palette.primary.main,
          zIndex: 0,
        },
        '&[data-pinned="true"]:not([role="columnheader"])': {
          zIndex: 1,
        },
        '&:not(:last-of-type)::after': {
          content: '""',
          position: 'absolute',
          right: 0,
          top: '20%',
          height: '60%',
          width: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          boxShadow: '0 0 4px rgba(255, 255, 255, 0.2)',
        },
      }),
    },
    // Override shared manual sorting to enable client-side sorting here
    manualSorting: false,
  });

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography>Please try refreshing the page or contact support if the problem persists.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        mt: { xs: 1, md: 2 },
      }}
    >
      {/* Header row - mirror Project Details Line Items layout when not fullscreen */}
      {!isFullScreen && (
        <Box mx={2} mb={1} display='flex' alignItems='center' justifyContent={'space-between'}>
          <Box display='flex' alignItems='center'>
            <Typography variant='h5' color='primary' sx={{ ml: 0, mb: 0 }}>
              Users
            </Typography>
          </Box>
          <Stack direction={'row'} spacing={1} alignItems='center'>
            <IconOverTextButton
              icon={<Search />}
              text='Search'
              tooltip='Search users'
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              sunken={isSearchOpen}
              showHoverEffect={true}
            />
            <Box
              sx={{
                ml: isSearchOpen ? 1 : 0,
                mr: 0,
                width: isSearchOpen ? '200px' : 0,
                transition: 'width 0.3s ease-in-out, opacity 0.3s ease-in-out, margin 0.3s ease-in-out',
                overflow: 'hidden',
                opacity: isSearchOpen ? 1 : 0,
              }}
            >
              <MRT_GlobalFilterTextField table={table} placeholder='Search' size='medium' InputProps={{ startAdornment: null }} />
            </Box>
            <Button variant='outlined' onClick={(e: React.MouseEvent<HTMLElement>) => setColumnsMenuAnchorEl(e.currentTarget)}>
              Manage Columns
            </Button>
            <Button
              variant='contained'
              startIcon={<PersonAdd />}
              onClick={handleOpenCreateUserDialog}
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                minWidth: 160,
              }}
            >
              Create User
            </Button>
            <MRT_ToggleFullScreenButton table={table} />
          </Stack>
        </Box>
      )}

      <MaterialReactTable table={table} />

      {/* Role Change Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem disabled>
          <Typography variant='subtitle2' fontWeight='bold'>
            Toggle Roles for {selectedUser?.fullName}
          </Typography>
        </MenuItem>
        {availableRoles.map(role => {
          const hasRole = selectedUser?.roles?.includes(role) || false;
          return (
            <MenuItem
              key={role}
              onClick={() => {
                if (selectedUser) {
                  handleRoleToggle(selectedUser, role);
                }
                handleMenuClose();
              }}
              sx={{
                backgroundColor: hasRole ? 'action.selected' : 'transparent',
                '&:hover': {
                  backgroundColor: hasRole ? 'action.hover' : 'action.hover',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='body2'>
                  {hasRole ? '✓' : '○'} {role}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>

      {/* Manage Columns menu */}
      {table && (
        <CustomManageColumnsMenu<any>
          anchorEl={columnsMenuAnchorEl}
          setAnchorEl={setColumnsMenuAnchorEl}
          table={table as any}
          title='Manage Columns'
        />
      )}

      {/* Create User Dialog */}
      <CreateUserDialog open={isCreateUserDialogOpen} onClose={handleCloseCreateUserDialog} />
    </Box>
  );
};

export default UsersList;
